import { motion } from "framer-motion";
import { Briefcase } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { waLink, WhatsAppIcon } from "@/lib/site";
import { T, useLang } from "@/lib/i18n";

export default function Faq() {
  const { lang } = useLang();
  const t = T[lang].faq;

  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 lg:py-32 bg-[#201B16] border-y border-stone-800/80 overflow-hidden">
      <img src="/images/faq-bg.webp" alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#16110C]/50" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#201B16] via-[#201B16]/25 to-[#201B16]" />
      <div className="relative max-w-4xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="text-center"
        >
          <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-[#C5A059] mb-4">{t.overline}</p>
          <h2 className="font-serif-display font-semibold tracking-tight text-stone-100 text-3xl sm:text-4xl lg:text-5xl">
            {t.h2a}<em className="not-italic text-[#C5A059]">{t.h2b}</em>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-12 rounded-3xl bg-[#2E2720] border border-stone-800 px-7 lg:px-10"
        >
          <Accordion type="single" collapsible data-testid="faq-accordion">
            {t.items.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-stone-800" data-testid={`faq-item-${i}`}>
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="font-serif-display text-lg sm:text-xl font-semibold text-stone-100 hover:text-[#C5A059] hover:no-underline py-6 transition-colors duration-300"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-content-${i}`} className="text-stone-400 text-[15px] leading-relaxed pb-6">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
          data-testid="b2b-banner"
          className="relative mt-10 rounded-3xl border border-[#C5A059]/35 overflow-hidden p-8 lg:p-10 flex flex-wrap items-center justify-between gap-6"
        >
          <img
            src="/images/wedding.webp"
            alt=""
            aria-hidden="true"
            loading="lazy" decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#16110C]/80" />
          <div className="relative flex items-start gap-5 max-w-xl">
            <span className="shrink-0 w-12 h-12 rounded-2xl bg-[#C5A059]/10 border border-[#C5A059]/40 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-[#C5A059]" />
            </span>
            <div>
              <h3 className="font-serif-display font-semibold text-2xl text-stone-100">{t.b2bTitle}</h3>
              <p className="mt-2 text-sm text-stone-400 leading-relaxed">{t.b2bText}</p>
            </div>
          </div>
          <a
            data-testid="b2b-whatsapp-button"
            href={waLink(t.b2bWa)}
            target="_blank"
            rel="noopener noreferrer"
            className="relative flex items-center gap-2 rounded-full bg-[#C5A059] text-[#141312] px-6 py-3.5 text-sm font-semibold hover:bg-[#D9B877] transition-colors duration-300"
          >
            <WhatsAppIcon className="w-4 h-4" />
            {t.b2bCta}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
